import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { usePatchSocieteReward } from '~/hooks/apis/rewards';

import type { SocieteReward, SocieteRewardCopayPrice, SocieteRewardPatchResult } from '~/services/LuxLoyalty';

import ClassicRewardsMarginLine from './ClassicRewardsMarginLine';
import CopayOverrideRow from './CopayOverrideRow';
import SocieteRewardSummary from './SocieteRewardSummary';
import ValuePerPointLine from './ValuePerPointLine';
import type { OverrideFormState } from './buildSocieteRewardAction';
import { buildInitialOverrideForm, buildSocieteRewardAction, diffValueFields } from './buildSocieteRewardAction';
import type { EligibilitySelection } from './eligibilitySectionState';
import { eligibilitySectionState } from './eligibilitySectionState';
import { formatExtraNightRate } from './formatExtraNightRate';
import { parseOverriddenFields } from './parseOverriddenFields';
import { AdminOverrideCaption, pointsFieldHelperText } from './pointsFieldHelperText';
import { CLEAR_OVERRIDE_DESCRIPTION } from './societeRewardActionInfo';
import { formulaRewardInputs } from './societeRewardMargin';
import { parseFormNumber } from './societeRewardOverrideFormHelpers';

/** Tooltip copy for the eligibility section's disabled "Auto" segment, keyed by why it's disabled. */
const AUTO_TOOLTIP_TEXT: Record<'owned' | 'values', string> = {
  owned: 'Eligibility is admin-owned — use CLEAR OVERRIDE to return it to the formula.',
  values: 'Overridden packages are no longer auto-calculated.',
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Salesforce external offer ID the package belongs to. */
  offerSfId: string;
  packageId: string;
  /** The package's current Société reward (live/formula values, and any existing override). */
  reward: SocieteReward;
  /** Current per-currency co-pay prices for the package. */
  prices: Array<SocieteRewardCopayPrice>;
  /** Currencies the package sells in — drives how many co-pay override rows are rendered. */
  sellingCurrencies: Array<string>;
  /** AUD cost-of-goods-sold for the package, used to live-preview the classic rewards margin. `null` hides a real figure behind "n/a". */
  cogsAud: number | null;
  /** AUD LuxPlus+ price for the package, used to live-preview value per point (VPP). `null` renders VPP as "n/a". */
  luxPlusPriceAud: number | null;
  /** Package's original trading margin percentage (0-100, addon-aware) — svc-offer's `margin_percentage`. Rendered as a static line in the stats block; omitted entirely when null/undefined. */
  marginPercentage?: number | null;
  /** Flash config `minMarginPercent` (0-100): the margin line below it renders in the warning color. `null`: no warning. */
  minMarginPercent: number | null;
  /** Flash config `vppFloorCents` — with `vppCeilingCents`, drives the VPP line's out-of-window warning. `null`: no window hint. */
  vppFloorCents: number | null;
  /** Flash config `vppCeilingCents` — see `vppFloorCents`. */
  vppCeilingCents: number | null;
  /** Called with the fresh patch result once an action is applied successfully, before the dialog closes. */
  onApplied: (fresh: SocieteRewardPatchResult) => void;
}

/** Converts the form's `eligible` tri-state into the toggle's selection value. */
function selectionFromEligible(eligible: boolean | null): EligibilitySelection {
  if (eligible === null) return 'auto';
  return eligible ? 'eligible' : 'ineligible';
}

/**
 * Dialog for applying an admin action to one package's Société flash-hotel reward: an override
 * (points/co-pay/eligibility) via `buildSocieteRewardAction`, or clearing an existing override via
 * a literal `clear_override` action behind a confirm step. The dialog only ever submits these two
 * actions — `disable`/`enable` stay on the `SocieteRewardAdminAction` type for API compatibility,
 * but the UI reaches the same outcome through an override's `eligible: false` (which yields
 * `ADMIN_INELIGIBLE` server-side, plus proper eligibility ownership) and `clear_override`.
 *
 * Eligibility behaves dynamically: once any points/co-pay value differs from the package's live
 * value, "Auto" eligibility becomes invalid (see `eligibilitySectionState`) — the Auto segment
 * disables and, if the admin hasn't made an explicit choice yet, "Eligible" is preselected for
 * them. Reverting those edits restores Auto. Once eligibility is already admin-owned, Auto stays
 * disabled regardless — CLEAR OVERRIDE is the only way back to it.
 */
export default function SocieteRewardOverrideDialog({
  open,
  onClose,
  offerSfId,
  packageId,
  reward,
  prices,
  sellingCurrencies,
  cogsAud,
  luxPlusPriceAud,
  marginPercentage,
  minMarginPercent,
  vppFloorCents,
  vppCeilingCents,
  onApplied,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();

  const [form, setForm] = useState<OverrideFormState>(() =>
    buildInitialOverrideForm(reward, prices, sellingCurrencies),
  );
  // The eligibility the form was seeded with on open — what "revert to the seeded value" restores
  // once the admin backs out of their points/co-pay edits without ever touching eligibility
  // themselves. See `eligibilitySectionState`.
  const [seededEligible, setSeededEligible] = useState<boolean | null>(() => form.eligible);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Tracks whether the admin has edited the base points or AUD base copay inputs since the dialog
  // last (re)seeded — the two inputs that feed the reward-margin/VPP "override" value. Reset
  // alongside the form itself; see the formula-vs-override `showBoth` rule below.
  const [formTouched, setFormTouched] = useState(false);
  // Tracks whether the admin has explicitly clicked the eligibility toggle since the dialog last
  // (re)opened — distinct from its value having only ever been auto-preselected. Drives whether
  // `eligibilitySectionState` is allowed to keep force-managing the selection. Reset on open.
  const [userTouchedEligibility, setUserTouchedEligibility] = useState(false);

  const patchMutation = usePatchSocieteReward(offerSfId, packageId);

  // Seed the form from the package's current live values whenever the dialog opens (or targets a
  // different package), so the admin sees — and can resubmit — the actual current points/co-pay
  // instead of blank inputs. Deliberately NOT keyed on `reward`/`prices`: while the dialog is open
  // and the admin is mid-edit, an updated `reward` prop must never clobber their in-progress input.
  useEffect(() => {
    if (open) {
      const initial = buildInitialOverrideForm(reward, prices, sellingCurrencies);
      setForm(initial);
      setSeededEligible(initial.eligible);
      setErrorMessage(null);
      setFormTouched(false);
      setUserTouchedEligibility(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, packageId]);

  // Live margin preview: override values take precedence over the package's current live values.
  const audPrice = prices.find((price) => price.currency_code === 'AUD');
  const effectivePointsBase = parseFormNumber(form.pointsBase) ?? reward.points_base;
  const effectiveCopayAud = parseFormNumber(form.copay.AUD?.base) ?? audPrice?.societe_reward_copay_base ?? null;

  // Formula-side inputs for the margin/VPP comparison: the reward's last-computed base points and
  // the AUD price row's server-computed base copay (see formulaRewardInputs for how its absence
  // is distinguished from an explicit, confirmed-zero null).
  const formulaInputs = formulaRewardInputs({ computedPointsBase: reward.computed_points_base, audPrice });

  // Both comparisons show a single (override) value until there's something to compare it
  // against: once the package is already overridden, or the admin has edited the points/copay
  // inputs that feed the comparison, show the formula value alongside the override value.
  const showFormulaVsOverride = reward.is_manual_override || formTouched;

  // Which fields the CURRENT override (if any) actually touches, so the form can badge an
  // overridden field vs a field still showing the frozen formula value.
  const overridden = parseOverriddenFields(reward.override_meta);

  // Whether any points/co-pay form value currently differs from the package's live value — the
  // same diff `buildSocieteRewardAction` uses to decide whether this action overrides a value
  // field. Drives the dynamic eligibility section below.
  const valueDiff = diffValueFields(form, { reward, prices });
  const valuesEdited =
    valueDiff.pointsBaseChanged || valueDiff.pointsExtraNightChanged || valueDiff.copayEntries.length > 0;

  const currentEligibleSelection = selectionFromEligible(form.eligible);
  const eligibilityState = eligibilitySectionState({
    valuesEdited,
    eligibilityOwned: overridden.eligible,
    seededEligible,
    currentSelection: currentEligibleSelection,
    userTouched: userTouchedEligibility,
  });

  // Apply whatever `eligibilitySectionState` decided the selection should be forced to. Guarded so
  // it only ever calls `setForm` when the forced value actually differs from the current one —
  // otherwise, since `eligibilityState` is recomputed fresh every render, this would re-fire (and
  // re-render) on every keystroke even when nothing needs to change.
  useEffect(() => {
    if (eligibilityState.forcedSelection !== undefined && eligibilityState.forcedSelection !== form.eligible) {
      setForm((current) => ({ ...current, eligible: eligibilityState.forcedSelection as boolean | null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibilityState.forcedSelection, form.eligible]);

  const setCopayField = (currency: string, field: 'base' | 'extraNight', value: string) => {
    setForm((current) => ({
      ...current,
      copay: { ...current.copay, [currency]: { ...current.copay[currency], [field]: value } },
    }));
    // Only the AUD base copay feeds the reward-margin/VPP "override" value — see `formTouched`.
    if (currency === 'AUD' && field === 'base') {
      setFormTouched(true);
    }
  };

  const applyAction = async (action: Parameters<typeof patchMutation.mutateAsync>[0], successMessage: string) => {
    setErrorMessage(null);
    try {
      const fresh = await patchMutation.mutateAsync(action);
      enqueueSnackbar(successMessage, { variant: 'success' });
      onApplied(fresh);
      onClose();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to apply change');
    }
  };

  const handleSubmit = async () => {
    // The tested builder is the gate for every override submit: an `{ error }` result renders
    // inline and never reaches the mutation.
    const result = buildSocieteRewardAction(form, { reward, prices });
    if ('error' in result) {
      setErrorMessage(result.error);
      return;
    }
    await applyAction(result.action, 'Société reward override applied');
  };

  const handleClearOverride = async () => {
    const confirmed = await showConfirmDialog({ title: 'Clear override?', description: CLEAR_OVERRIDE_DESCRIPTION });
    if (!confirmed) return;
    await applyAction({ action: 'clear_override' }, 'Override cleared');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" component="span" fontWeight={600}>
          Société reward override
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <SocieteRewardSummary reward={reward} prices={prices} dense />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Points override
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                <TextField
                  label="Base points"
                  value={form.pointsBase}
                  onChange={(e) => {
                    setForm((current) => ({ ...current, pointsBase: e.target.value }));
                    setFormTouched(true);
                  }}
                  placeholder={String(reward.points_base)}
                  helperText={pointsFieldHelperText(
                    reward.is_manual_override,
                    overridden.pointsBase,
                    reward.computed_points_base,
                  )}
                  fullWidth
                />
                <TextField
                  label="Extra-night points"
                  value={form.pointsExtraNight}
                  onChange={(e) => setForm((current) => ({ ...current, pointsExtraNight: e.target.value }))}
                  placeholder={formatExtraNightRate(reward.points_extra_night)}
                  helperText={pointsFieldHelperText(
                    reward.is_manual_override,
                    overridden.pointsExtraNight,
                    reward.computed_points_extra_night,
                  )}
                  fullWidth
                />
              </Box>

              {sellingCurrencies.length > 0 && (
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Co-pay overrides
                  </Typography>
                  {sellingCurrencies.map((currency) => (
                    <CopayOverrideRow
                      key={currency}
                      currency={currency}
                      value={form.copay[currency] ?? { base: '', extraNight: '' }}
                      livePrice={prices.find((price) => price.currency_code === currency)}
                      onChange={(field, value) => setCopayField(currency, field, value)}
                      isOverridden={reward.is_manual_override && overridden.copay.has(currency)}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Eligibility override
            </Typography>
            <ToggleButtonGroup
              value={currentEligibleSelection}
              exclusive
              onChange={(_, value) => {
                if (!value) return;
                setUserTouchedEligibility(true);
                setForm((current) => ({ ...current, eligible: value === 'auto' ? null : value === 'eligible' }));
              }}
              size="small"
              color="primary"
              fullWidth
            >
              {eligibilityState.autoDisabled ? (
                <Tooltip title={eligibilityState.autoTooltip ? AUTO_TOOLTIP_TEXT[eligibilityState.autoTooltip] : ''}>
                  <span>
                    <ToggleButton value="auto" disabled>
                      Auto
                    </ToggleButton>
                  </span>
                </Tooltip>
              ) : (
                <ToggleButton value="auto">Auto</ToggleButton>
              )}
              <ToggleButton value="eligible">Eligible</ToggleButton>
              <ToggleButton value="ineligible">Ineligible</ToggleButton>
            </ToggleButtonGroup>
            {reward.is_manual_override && overridden.eligible && (
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                <AdminOverrideCaption />
              </Typography>
            )}
          </Box>

          <TextField
            label="Reason"
            value={form.reason}
            onChange={(e) => setForm((current) => ({ ...current, reason: e.target.value }))}
            required
            fullWidth
            multiline
            minRows={2}
            placeholder="Why is this package being overridden?"
          />

          <Stack spacing={0.25}>
            {marginPercentage != null && (
              <Typography variant="body2" color="text.secondary">
                Package margin: {marginPercentage.toFixed(1)}%
              </Typography>
            )}
            <ClassicRewardsMarginLine
              cogsAud={cogsAud}
              formulaPoints={formulaInputs.points}
              formulaCopayAud={formulaInputs.copayAud}
              formulaCopayAvailable={formulaInputs.copayAvailable}
              overridePoints={effectivePointsBase}
              overrideCopayAud={effectiveCopayAud}
              minMarginPercent={minMarginPercent}
              showBoth={showFormulaVsOverride}
            />
            <ValuePerPointLine
              luxPlusPriceAud={luxPlusPriceAud}
              formulaPoints={formulaInputs.points}
              overridePoints={effectivePointsBase}
              showBoth={showFormulaVsOverride}
              vppFloorCents={vppFloorCents}
              vppCeilingCents={vppCeilingCents}
            />
          </Stack>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <Divider />

          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Box>
              {reward.is_manual_override && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearOverride}
                  disabled={patchMutation.isPending}
                >
                  Clear override
                </Button>
              )}
            </Box>
            <Box display="flex" gap={1}>
              <Button variant="text" onClick={onClose} disabled={patchMutation.isPending}>
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                onClick={handleSubmit}
                loading={patchMutation.isPending}
                sx={{ px: 4 }}
              >
                Override
              </LoadingButton>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
